import { IconSparkles } from '@tabler/icons-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import type { AccountSuspensionDetails } from '@/features/customer-care/accountSuspension';

interface AiSummaryCardProps {
  summary: string | null;
  isLoading: boolean;
  error: string | null;
  suspension?: AccountSuspensionDetails | null;
}

export function AiSummaryCard({
  summary,
  isLoading,
  error,
  suspension,
}: AiSummaryCardProps) {
  return (
    <Card className="bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[200px] h-[200px] bg-[radial-gradient(circle,rgba(255,255,255,0.1)_0%,transparent_70%)] rounded-full translate-x-1/2 -translate-y-1/2" />
      <CardContent className="relative z-10 p-6">
        <div className="flex items-center gap-3 mb-4">
          <IconSparkles className="h-7 w-7" />
          <h3 className="text-lg font-bold">AI Customer Insights</h3>
        </div>

        {suspension && (
          <div className="mb-4 space-y-1" role="status">
            <p className="font-semibold">
              {suspension.isRica ? 'RICA suspended' : 'Account suspended'}
              {' — '}
              {suspension.reason || 'Suspension reason not recorded.'}
            </p>
            <p className="text-sm">
              Suspended on:{' '}
              {suspension.createdAt
                ? new Date(suspension.createdAt).toLocaleString()
                : 'Date not recorded'}
            </p>
            {suspension.performedBy && (
              <p className="text-sm">Suspended by: {suspension.performedBy}</p>
            )}
          </div>
        )}

        {(isLoading || (!summary && !error)) && (
          <div className="space-y-2">
            <Skeleton className="h-6 w-[90%] bg-white/20" />
            <Skeleton className="h-6 w-full bg-white/20" />
            <Skeleton className="h-6 w-[85%] bg-white/20" />
            <Skeleton className="h-6 w-[95%] bg-white/20" />
          </div>
        )}

        {error && (
          <Alert className="bg-white/90 text-foreground">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {summary && (
          <ul className="m-0 pl-5 space-y-2">
            {summary.split('\n').map((line, index) => {
              const cleanedLine = line.replace(/^[\*\-]\s*/, '').trim();
              if (cleanedLine) {
                return (
                  <li key={index} className="leading-relaxed text-[0.95rem]">
                    {cleanedLine}
                  </li>
                );
              }
              return null;
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
